import { useState } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Card, CardContent } from "./ui/card";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const QuoteForm = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    pickupPostcode: "",
    dropoffPostcode: "",
    carMake: "",
    carModel: "",
    serviceType: "",
    carCondition: "",
    additionalInfo: ""
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await axios.post(`${BACKEND_URL}/api/quotes`, formData);
      
      if (response.status === 200) {
        toast.success("Quote Request Received!", {
          description: "We'll contact you shortly with a competitive quote. Check your email for confirmation.",
          duration: 5000,
        });
        
        // Reset form
        setFormData({
          name: "",
          email: "",
          phone: "",
          pickupPostcode: "",
          dropoffPostcode: "",
          carMake: "",
          carModel: "",
          serviceType: "",
          carCondition: "",
          additionalInfo: ""
        });
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error("Error submitting quote:", error);
      }
      toast.error("Submission Failed", {
        description: error.response?.data?.detail || "Please try again or call us at 0800 123 4567",
        duration: 5000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <section id="quote" className="py-20 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <span className="text-slate-300 font-semibold uppercase tracking-wider text-sm">
            Get Started
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-white mt-3 mb-4">
            Request Your Free Quote
          </h2>
          <p className="text-lg text-slate-300 max-w-2xl mx-auto">
            Fill in the details below and we'll get back to you with a competitive quote
          </p>
        </div>

        <Card className="max-w-4xl mx-auto border-slate-700 bg-white/95 backdrop-blur-sm">
          <CardContent className="p-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Contact Information */}
              <div className="grid md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Full Name *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => handleChange("name", e.target.value)}
                    placeholder="John Smith"
                    required
                    disabled={isSubmitting}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleChange("email", e.target.value)}
                    placeholder="john@example.com"
                    required
                    disabled={isSubmitting}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone Number *</Label>
                  <Input
                    id="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => handleChange("phone", e.target.value)}
                    placeholder="07123 456789"
                    required
                    disabled={isSubmitting}
                  />
                </div>
              </div>

              {/* Location Details */}
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="pickupPostcode">Pickup Postcode *</Label>
                  <Input
                    id="pickupPostcode"
                    value={formData.pickupPostcode}
                    onChange={(e) => handleChange("pickupPostcode", e.target.value)}
                    placeholder="SW1A 1AA"
                    required
                    disabled={isSubmitting}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="dropoffPostcode">Drop-off Postcode *</Label>
                  <Input
                    id="dropoffPostcode"
                    value={formData.dropoffPostcode}
                    onChange={(e) => handleChange("dropoffPostcode", e.target.value)}
                    placeholder="M1 1AD"
                    required
                    disabled={isSubmitting}
                  />
                </div>
              </div>

              {/* Vehicle Details */}
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="carMake">Car Make *</Label>
                  <Input
                    id="carMake"
                    value={formData.carMake}
                    onChange={(e) => handleChange("carMake", e.target.value)}
                    placeholder="e.g., Ford, BMW, Mercedes"
                    required
                    disabled={isSubmitting}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="carModel">Car Model *</Label>
                  <Input
                    id="carModel"
                    value={formData.carModel}
                    onChange={(e) => handleChange("carModel", e.target.value)}
                    placeholder="e.g., Focus, 3 Series, C-Class"
                    required
                    disabled={isSubmitting}
                  />
                </div>
              </div>

              {/* Service Type and Car Condition */}
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="serviceType">Service Required *</Label>
                  <Select 
                    value={formData.serviceType} 
                    onValueChange={(value) => handleChange("serviceType", value)} 
                    required
                    disabled={isSubmitting}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select service type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="breakdown">Roadside Breakdown & Recovery</SelectItem>
                      <SelectItem value="auction">Car Auction Transport</SelectItem>
                      <SelectItem value="transport">Vehicle Transportation</SelectItem>
                      <SelectItem value="jumpstart">Jump Start Service</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="carCondition">Car Condition *</Label>
                  <Select 
                    value={formData.carCondition} 
                    onValueChange={(value) => handleChange("carCondition", value)} 
                    required
                    disabled={isSubmitting}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select car condition" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="running">Running / Drives</SelectItem>
                      <SelectItem value="non-running">Non-Running / Needs Towing</SelectItem>
                      <SelectItem value="rolls">Rolls in Neutral</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Additional Information */}
              <div className="space-y-2">
                <Label htmlFor="additionalInfo">Additional Information</Label>
                <Textarea
                  id="additionalInfo"
                  value={formData.additionalInfo}
                  onChange={(e) => handleChange("additionalInfo", e.target.value)}
                  placeholder="Any special requirements or additional details..."
                  rows={4}
                  disabled={isSubmitting}
                />
              </div>

              <Button 
                type="submit" 
                size="lg"
                className="w-full bg-slate-900 hover:bg-slate-800 text-white text-lg py-6"
                disabled={isSubmitting}
              >
                {isSubmitting ? "Submitting..." : "Request Free Quote"}
              </Button>

              <p className="text-sm text-slate-500 text-center">
                By submitting this form, you agree to be contacted regarding your quote request
              </p>
            </form>
          </CardContent>
        </Card>
      </div>
    </section>
  );
};
